import { useCallback, useState } from 'react';

/** `system` follows the OS (the library's default: no `data-theme` on `<html>`). */
export type ThemeChoice = 'dark' | 'light' | 'system';

export const THEME_STORAGE_KEY = 'ark.theme';

const CHOICES: readonly ThemeChoice[] = ['dark', 'light', 'system'];

function storage(): Storage | null {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
        return null;
    }
}

/** The stored choice; `system` when nothing (or nothing valid) is stored. */
export function readTheme(): ThemeChoice {
    try {
        const value = storage()?.getItem(THEME_STORAGE_KEY);
        return CHOICES.includes(value as ThemeChoice) ? (value as ThemeChoice) : 'system';
    } catch {
        return 'system';
    }
}

/** Sets `<html data-theme>` (`dark` / `light`) or removes it (`system`); the library tokens do the rest. */
export function applyTheme(choice: ThemeChoice, root: HTMLElement = document.documentElement): void {
    if (choice === 'system') delete root.dataset.theme;
    else root.dataset.theme = choice;
}

/** Applies and remembers the choice (`localStorage['ark.theme']`; `system` clears it). */
export function saveTheme(choice: ThemeChoice): void {
    applyTheme(choice);
    try {
        if (choice === 'system') storage()?.removeItem(THEME_STORAGE_KEY);
        else storage()?.setItem(THEME_STORAGE_KEY, choice);
    } catch {
        // Storage blocked (private mode): the choice still applies to this page.
    }
}

/** The current choice and a setter that applies and persists it. `src/index.tsx` applies the stored one at startup. */
export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
    const [theme, setThemeState] = useState<ThemeChoice>(readTheme);
    const setTheme = useCallback((choice: ThemeChoice) => {
        saveTheme(choice);
        setThemeState(choice);
    }, []);
    return [theme, setTheme];
}
